import React, { useEffect, useState, createContext, useContext } from 'react';
export interface CartItem {
  id: string;
  name: string;
  description: string;
  price: number;
  type: 'premium-report' | 'package' | 'consultation' | 'course' | 'marketplace-product';
  serviceName?: string;
  astrologer?: {
    name: string;
    initials: string;
    rating: number;
    reviews: number;
  };
  format?: string;
  duration?: string;
  badges?: {
    label: string;
    variant: 'pro' | 'success' | 'warning' | 'neutral' | 'primary' | 'secondary' | 'outline';
  }[];
  oldPrice?: number;
}
const CART_STORAGE_KEY = 'astriqo_cart';
const CART_ITEMS_STORAGE_KEY = 'astriqo_cart_items';
function loadItemsFromStorage(): CartItem[] {
  try {
    const stored = localStorage.getItem(CART_ITEMS_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored) as CartItem[];
    }
    const oldStored = localStorage.getItem(CART_STORAGE_KEY);
    if (oldStored) {
      const item = JSON.parse(oldStored) as CartItem;
      localStorage.removeItem(CART_STORAGE_KEY);
      return [item];
    }
  } catch {
    // Ignore parse errors
  }
  return [];
}
function saveItemsToStorage(items: CartItem[]) {
  try {
    if (items.length > 0) {
      localStorage.setItem(CART_ITEMS_STORAGE_KEY, JSON.stringify(items));
    } else {
      localStorage.removeItem(CART_ITEMS_STORAGE_KEY);
    }
    localStorage.removeItem(CART_STORAGE_KEY);
  } catch {
    // Ignore storage errors
  }
}
interface CartContextType {
  items: CartItem[];
  addItem: (item: CartItem) => void;
  removeItem: (id: string) => void;
  clearCart: () => void;
  itemCount: number;
  totalPrice: number;
  mainItem: CartItem | null;
  setCartItem: (item: CartItem) => void;
}
const CartContext = createContext<CartContextType | undefined>(undefined);
export function CartProvider({
  children
}: {
  children: ReactNode;
}) {
  const [items, setItems] = useState<CartItem[]>(() => loadItemsFromStorage());
  useEffect(() => {
    saveItemsToStorage(items);
  }, [items]);
  const addItem = (item: CartItem) => {
    setItems(prev => {
      const exists = prev.find(i => i.id === item.id);
      if (exists) return prev;
      return [...prev, item];
    });
  };
  const removeItem = (id: string) => {
    setItems(prev => prev.filter(i => i.id !== id));
  };
  const clearCart = () => {
    setItems([]);
  };
  const setCartItem = (item: CartItem) => {
    setItems([item]);
  };
  const mainItem = items.length > 0 ? items[0] : null;
  const itemCount = items.length;
  const totalPrice = items.reduce((sum, i) => sum + i.price, 0);
  return <CartContext.Provider value={{
    items,
    addItem,
    removeItem,
    clearCart,
    itemCount,
    totalPrice,
    mainItem,
    setCartItem
  }} data-id="element-18093">
      {children}
    </CartContext.Provider>;
}
export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}